import React from 'react';
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  DateField,

  ReferenceField,
  FunctionField,
  ChipField,
} from 'react-admin';

const paymentStatusChoices = [
  { id: 'pending', name: 'Ожидает' },
  { id: 'processing', name: 'Обрабатывается' },
  { id: 'completed', name: 'Завершен' },
  { id: 'failed', name: 'Неудачен' },
  { id: 'cancelled', name: 'Отменен' },
  { id: 'refunded', name: 'Возвращен' },
];

const paymentMethodChoices = [
  { id: 'card', name: 'Карта' },
  { id: 'crypto', name: 'Криптовалюта' },
];

const paymentProviderChoices = [
  { id: 'stripe', name: 'Stripe' },
  { id: 'coinbase', name: 'Coinbase' },
];

const StatusChip = ({ record }: any) => {
  const statusChoice = paymentStatusChoices.find(choice => choice.id === record.status);
  const getColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      case 'refunded': return 'secondary';
      default: return 'default';
    }
  };
  
  return (
    <ChipField 
      record={{ value: statusChoice?.name || record.status }} 
      source="value" 
      color={getColor(record.status)}
    />
  );
};

const AmountField = ({ record }: any) => (
  <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
    {record.amount?.toFixed(2)} {record.currency?.toUpperCase()}
  </span>
);

const MethodChip = ({ record }: any) => {
  const methodChoice = paymentMethodChoices.find(choice => choice.id === record.paymentMethod);
  return <ChipField record={{ value: methodChoice?.name || record.paymentMethod }} source="value" />;
};

const ProviderChip = ({ record }: any) => {
  const providerChoice = paymentProviderChoices.find(choice => choice.id === record.provider);
  return <ChipField record={{ value: providerChoice?.name || record.provider }} source="value" />;
};

export const PaymentShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Основная информация">
        <TextField source="id" label="ID транзакции" />
        <AmountField label="Сумма" />
        <StatusChip label="Статус" />
        <MethodChip label="Способ оплаты" />
        <ProviderChip label="Провайдер" />
        
        <DateField source="createdAt" label="Создана" showTime />
        <DateField source="updatedAt" label="Обновлена" showTime />
        
        <TextField source="failureReason" label="Причина ошибки" />
      </Tab>

      <Tab label="Клиент и заказ">
        <ReferenceField source="userId" reference="users" label="Клиент">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="userId" reference="users" label="Email клиента">
          <TextField source="email" />
        </ReferenceField>
        
        <ReferenceField source="orderId" reference="supplementOrders" label="ID заказа">
          <TextField source="id" />
        </ReferenceField>
        <ReferenceField source="orderId" reference="supplementOrders" label="Статус заказа">
          <TextField source="status" />
        </ReferenceField>
        <ReferenceField source="orderId" reference="supplementOrders" label="Добавка">
          <TextField source="supplement.name" />
        </ReferenceField>
      </Tab>

      <Tab label="Данные провайдера">
        <TextField source="providerTransactionId" label="ID транзакции у провайдера" />
        <TextField source="providerPayload" label="Данные провайдера (JSON)" />
        
        <FunctionField 
          label="Данные провайдера (форматированные)"
          render={(record: any) => {
            try {
              if (record.providerPayload) {
                const payload = JSON.parse(record.providerPayload);
                return (
                  <pre style={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '10px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    {JSON.stringify(payload, null, 2)}
                  </pre>
                );
              }
              return 'Данные отсутствуют';
            } catch (e) {
              return record.providerPayload || 'Данные отсутствуют';
            }
          }}
        />
      </Tab>

      <Tab label="Метаданные">
        <TextField source="metadata" label="Метаданные (JSON)" />
        
        <FunctionField 
          label="Метаданные (форматированные)"
          render={(record: any) => {
            try {
              if (record.metadata) {
                const metadata = JSON.parse(record.metadata);
                return (
                  <div>
                    {Object.entries(metadata).map(([key, value]: [string, any]) => (
                      <div key={key} style={{ marginBottom: '5px' }}>
                        <strong>{key}:</strong> {String(value)}
                      </div>
                    ))}
                  </div>
                );
              }
              return 'Метаданные отсутствуют';
            } catch (e) {
              return record.metadata || 'Метаданные отсутствуют';
            }
          }}
        />
      </Tab>

      <Tab label="История изменений">
        <FunctionField 
          label="Временная шкала"
          render={(record: any) => (
            <div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Создана:</strong> {new Date(record.createdAt).toLocaleString()}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Последнее обновление:</strong> {new Date(record.updatedAt).toLocaleString()}
              </div>
              {record.status === 'completed' && (
                <div style={{ color: 'green', fontWeight: 'bold' }}>
                  ✅ Платеж успешно завершен
                </div>
              )}
              {record.status === 'failed' && (
                <div style={{ color: 'red', fontWeight: 'bold' }}>
                  ❌ Платеж неудачен: {record.failureReason || 'Причина не указана'}
                </div>
              )}
              {record.status === 'refunded' && (
                <div style={{ color: 'orange', fontWeight: 'bold' }}>
                  🔄 Платеж возвращен
                </div>
              )}
            </div>
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
